import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import Filters from 'components/Filters';
import iconAdd from 'images/icon_add_new.svg';
import ListView from 'components/ListView';
import Portal from 'portal/Portal';
import styles from './BasePanel.module.scss';

const BasePanel = ({
  onEdit,
  toggleModal,
  editable,
  //start filters
  filtersRequired,
  filters,
  setFilters,
  defaultFilterValues,
  filterValues,
  // end filters
  onUpdate,
  header,
  cols,
  data,
  DetailComponent,
  FormComponent,
  operationComponent,
  enhancedCancellation,
  downloadable,
  onDownloadClick,
  fetchData,
  rowModelType,
  pagination,
  forceRefresh,
  setForceRefresh,
  totalEntries,
  createTextHover,
  downloadHoverText,
  setGridAPI,
  fitColumnsToSize,
  enhancedOnSelect,
  uploadable,
  onUploadClick,
  uploadHoverText,
  limitTags,
  filterKey,
}) => {
  const [picked, setPicked] = useState();
  const [detailOpen, setDetailOpen] = useState(false);
  const [agGridApi, setApi] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [filterReset, setFilterReset] = useState(false);
  const toggleEditMode = () => {
    setEditMode(true);
  };

  const reset = () => {
    setFilterReset(true);
    // This is triggering the request fetchlist again. Might need it again
    // onUpdate(defaultFilterValues);
  };

  const onSelect = () => {
    if (!DetailComponent) {
      agGridApi.deselectAll();
      return;
    }
    const selectedData = agGridApi.getSelectedRows();
    setPicked(selectedData[0]);
    setDetailOpen(true);
    enhancedOnSelect &&
      typeof enhancedOnSelect === 'function' &&
      enhancedOnSelect(selectedData[0]);
  };

  const closeForm = () => {
    setEditMode(false);
    toggleModal();
  };

  const cancel = () => {
    if (enhancedCancellation) {
      enhancedCancellation();
    }
    agGridApi.deselectAll();
    setDetailOpen(false);
    setEditMode(false);
  };
  //this is to to fetch the updated data when the list data is getting updated from detail panel
  const getPickedData = () => {
    return data.find(singleListData => singleListData.id === picked.id);
  };

  return (
    <Fragment>
      <div className={styles.container}>
        {filtersRequired && (
          <Filters
            id={`${header}_filter`}
            editable={editable}
            reset={reset}
            filters={filters}
            filterValues={filterValues}
            setFilters={setFilters}
            icon={iconAdd}
            onIconClick={toggleModal}
            header={header}
            onUpdate={onUpdate}
            downloadable={downloadable}
            onDownloadClick={onDownloadClick}
            createTextHover={createTextHover}
            downloadHoverText={downloadHoverText}
            uploadable={uploadable}
            onUploadClick={onUploadClick}
            uploadHoverText={uploadHoverText}
            defaultFilterValues={defaultFilterValues}
            limitTags={limitTags}
          />
        )}
        <ListView
          agGridApi={agGridApi}
          setApi={setApi}
          onSelect={onSelect}
          rowData={data}
          columns={cols}
          fetchData={fetchData}
          rowModelType={rowModelType}
          pagination={pagination}
          forceRefresh={forceRefresh}
          setForceRefresh={setForceRefresh}
          totalEntries={totalEntries}
          setGridAPI={setGridAPI}
          fitColumnsToSize={fitColumnsToSize}
          filterKey={filterKey}
          isFilterReset={filterReset}
          setFilterReset={setFilterReset}
        />
        {DetailComponent &&
          picked &&
          detailOpen &&
          getPickedData() !== undefined && (
            <DetailComponent
              id={picked.id}
              selected={getPickedData()}
              cancel={cancel}
              editable={editable}
              toggleEditMode={toggleEditMode}
              operationComponent={operationComponent}
              downloadable={downloadable}
            />
          )}
      </div>
      {onEdit && (
        <Portal id={'portal'} type="modal">
          {
            <FormComponent
              id={picked ? picked.id : undefined}
              editMode={editMode}
              closeForm={closeForm}
            />
          }
        </Portal>
      )}
    </Fragment>
  );
};

BasePanel.propTypes = {
  header: PropTypes.string,
  filters: PropTypes.array,
  cols: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  DetailComponent: PropTypes.elementType,
  FormComponent: PropTypes.elementType,
  operationComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  filterValues: PropTypes.object,
  filtersRequired: PropTypes.bool,
  setFilters: PropTypes.func,
  editable: PropTypes.bool,
  filterKey: PropTypes.bool,
  onUpdate: PropTypes.func,
  onEdit: PropTypes.bool,
  toggleModal: PropTypes.func,
  enhancedCancellation: PropTypes.func,
  downloadable: PropTypes.bool,
  onDownloadClick: PropTypes.func,
  uploadable: PropTypes.bool,
  onUploadClick: PropTypes.func,
  uploadHoverText: PropTypes.string,
  fetchData: PropTypes.func,
  rowModelType: PropTypes.string,
  pagination: PropTypes.bool,
  forceRefresh: PropTypes.bool,
  setForceRefresh: PropTypes.func,
  totalEntries: PropTypes.number,
  createTextHover: PropTypes.string,
  downloadHoverText: PropTypes.string,
  setGridAPI: PropTypes.func,
  fitColumnsToSize: PropTypes.bool,
  enhancedOnSelect: PropTypes.func,
  defaultFilterValues: PropTypes.object,
  limitTags: PropTypes.number,
};

BasePanel.defaultProps = {
  filtersRequired: false,
  editable: true,
  pagination: true,
};

export default BasePanel;
